/* Sold-out rescue box.
 * Primary: cross-sell in-stock alternatives pulled from Shopify's
 * recommendations API. Secondary: Klaviyo back-in-stock signup via the
 * client API. The Klaviyo company id is read from the app embed's script
 * tag, so nothing is hardcoded per store. If Klaviyo isn't installed the
 * signup hides itself; if no in-stock alternatives exist the cross-sell
 * stays hidden and the "see similar" scroll link shows instead.
 */
(function () {
  if (customElements.get('back-in-stock-form')) return;

  function klaviyoCompanyId() {
    var script = document.querySelector('script[src*="klaviyo.com/onsite/js/"]');
    if (!script) return null;
    try {
      return new URL(script.src, window.location.origin).searchParams.get('company_id');
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
    }).format(cents / 100);
  }

  function isMembership(variant, type) {
    return variant.option1 === type || variant.option2 === type || variant.option3 === type;
  }

  function imageUrl(url, width) {
    if (!url) return null;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.form = this.querySelector('.back-in-stock__form');
      this.message = this.querySelector('.back-in-stock__message');
      this.relatedLink = this.querySelector('.back-in-stock__related');
      this.crosssell = this.querySelector('.back-in-stock__crosssell');
      this.crosssellLoaded = false;

      if (this.form) {
        var submit = this.onSubmit.bind(this);
        var button = this.form.querySelector('.back-in-stock__submit');
        if (button) button.addEventListener('click', submit);
        var input = this.form.querySelector('input[name="email"]');
        if (input) {
          input.addEventListener('keydown', function (event) {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit(event);
            }
          });
        }
        if (!klaviyoCompanyId()) this.form.hidden = true;
      }

      this.setupRelatedLink();

      // Dual-button PDPs announce pair availability on variant change.
      this.onPairChange = this.onPairChange.bind(this);
      document.addEventListener('pair-availability:change', this.onPairChange);

      // Single-button PDPs: mirror the add-to-cart button's disabled state.
      this.observeAtcButton();

      if (!this.classList.contains('hidden')) this.loadCrosssell();
    }

    disconnectedCallback() {
      document.removeEventListener('pair-availability:change', this.onPairChange);
      if (this.atcObserver) this.atcObserver.disconnect();
    }

    onPairChange(event) {
      var detail = event.detail || {};
      // Only dual-button forms carry membership ids; ignore the event otherwise.
      if (!this.dataset.memberVariantId && !this.dataset.nonmemberVariantId) return;
      if (detail.memberVariantId) this.dataset.memberVariantId = detail.memberVariantId;
      if (detail.nonMemberVariantId) this.dataset.nonmemberVariantId = detail.nonMemberVariantId;
      if (detail.variantId) this.dataset.variantId = detail.variantId;
      this.classList.toggle('hidden', !detail.soldOut);
      if (detail.soldOut) this.loadCrosssell();
    }

    observeAtcButton() {
      if (this.dataset.memberVariantId || this.dataset.nonmemberVariantId) return;
      var scope = this.closest('.product-form__buttons') || this.closest('form');
      var button = scope ? scope.querySelector('button[name="add"]') : null;
      if (!button) return;

      var self = this;
      var soldOutLabel = ((window.variantStrings && window.variantStrings.soldOut) || 'sold out')
        .trim()
        .toLowerCase();
      this.atcObserver = new MutationObserver(function () {
        // The theme disables the button while re-rendering on every variant
        // switch; only a disabled button actually labelled "Sold out" means
        // the selection is unavailable.
        var label = (button.textContent || '').toLowerCase();
        var soldOut = button.disabled && label.indexOf(soldOutLabel) !== -1;
        self.classList.toggle('hidden', !soldOut);
        // Sold out hides the whole ATC stack (button + quantity). Keyed here on
        // the settled sold-out state, not data-unavailable, which Shrine core
        // toggles as a loading flag mid-update.
        var wrap = button.closest('.product-form__quantity-and-btn');
        if (wrap) wrap.toggleAttribute('data-oos-hidden', soldOut);
        if (soldOut) self.loadCrosssell();
        var form = button.closest('form');
        var variantInput = form ? form.querySelector('input[name="id"]') : null;
        if (variantInput && variantInput.value) self.dataset.variantId = variantInput.value;
      });
      // Watch the label too: switching straight between two sold-out variants
      // changes the text without ever toggling the disabled attribute.
      this.atcObserver.observe(button, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    loadCrosssell() {
      if (!this.crosssell || this.crosssellLoaded || !this.dataset.crosssellUrl) return;
      this.crosssellLoaded = true;

      var self = this;
      fetch(this.dataset.crosssellUrl)
        .then(function (response) { return response.json(); })
        .then(function (data) {
          var currentId = self.dataset.productId;
          var products = (data.products || []).filter(function (p) {
            if (!p.available) return false;
            if (currentId && String(p.id) === String(currentId)) return false;
            if (p.tags && p.tags.indexOf('collection-hidden') !== -1) return false;
            return true;
          });
          // Funnel-only "offer" products don't carry Member/Non-Member variants;
          // prefer real catalog (membership) products when any are present.
          var membership = products.filter(function (p) {
            return (p.variants || []).some(function (v) {
              return isMembership(v, 'Member') || isMembership(v, 'Non-Member');
            });
          });
          if (membership.length) products = membership;

          var count = parseInt(self.dataset.crosssellCount, 10) || 1;
          self.renderCrosssell(products.slice(0, count));
        })
        .catch(function () { /* leave the cross-sell hidden */ });
    }

    renderCrosssell(products) {
      if (!products.length) return;
      var cardsContainer = this.crosssell.querySelector('.back-in-stock__crosssell-cards');
      var self = this;

      products.forEach(function (product) {
        var available = product.variants.filter(function (v) { return v.available; });
        var member = available.find(function (v) { return isMembership(v, 'Member'); });
        var nonMember = available.find(function (v) { return isMembership(v, 'Non-Member'); });
        var priceVariant = member || nonMember || available[0];
        if (!priceVariant) return;

        var card = document.createElement('a');
        card.className = 'back-in-stock__card';
        card.href = product.url.split('?')[0];

        var img = document.createElement('img');
        img.className = 'back-in-stock__card-image';
        img.src = imageUrl(product.featured_image, 180) || '';
        img.alt = product.title;
        img.loading = 'lazy';
        card.appendChild(img);

        var info = document.createElement('div');
        info.className = 'back-in-stock__card-info';

        var title = document.createElement('p');
        title.className = 'back-in-stock__card-title';
        title.textContent = product.title;
        info.appendChild(title);

        var price = document.createElement('p');
        price.className = 'back-in-stock__card-price';
        price.textContent = formatMoney(priceVariant.price);
        if (member) {
          var vip = document.createElement('span');
          vip.className = 'back-in-stock__card-vip';
          vip.textContent = 'VIP';
          price.appendChild(vip);
        }
        var compareAt = priceVariant.compare_at_price || product.compare_at_price;
        if (compareAt && compareAt > priceVariant.price) {
          var compare = document.createElement('span');
          compare.className = 'back-in-stock__card-compare';
          compare.textContent = formatMoney(compareAt);
          price.appendChild(compare);
        }
        info.appendChild(price);
        card.appendChild(info);

        var arrow = document.createElement('span');
        arrow.className = 'back-in-stock__card-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        card.appendChild(arrow);

        cardsContainer.appendChild(card);
      });

      if (cardsContainer.children.length) {
        this.crosssell.hidden = false;
        // Cross-sell replaces the scroll-down link as the "somewhere to go".
        if (this.relatedLink) this.relatedLink.hidden = true;
      }
    }

    setupRelatedLink() {
      if (!this.relatedLink) return;
      var target = document.querySelector('product-recommendations, .related-products');
      if (!target) return;
      this.relatedLink.hidden = false;
      this.relatedLink.addEventListener('click', function (event) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    targetVariantId() {
      var memberMode = document.body.classList.contains('member-mode');
      if (memberMode && this.dataset.memberVariantId) return this.dataset.memberVariantId;
      if (!memberMode && this.dataset.nonmemberVariantId) return this.dataset.nonmemberVariantId;
      return this.dataset.variantId || this.dataset.nonmemberVariantId || this.dataset.memberVariantId;
    }

    showMessage(text) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.hidden = false;
    }

    onSubmit(event) {
      event.preventDefault();
      var companyId = klaviyoCompanyId();
      var input = this.form.querySelector('input[name="email"]');
      var email = input.value.trim();
      if (!email || (input.validity && !input.validity.valid)) {
        this.showMessage('Please enter a valid email address.');
        return;
      }
      var variantId = this.targetVariantId();
      if (!companyId || !variantId) {
        this.showMessage('Something went wrong. Please try again.');
        return;
      }

      var self = this;
      var submitButton = this.form.querySelector('.back-in-stock__submit');
      if (submitButton) submitButton.disabled = true;

      fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email } } },
              channels: ['EMAIL']
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
              }
            }
          }
        })
      })
        .then(function (response) {
          if (response.status === 202) {
            self.form.hidden = true;
            self.showMessage(self.dataset.successMessage || "You're on the list!");
          } else {
            return response.json().then(function (body) {
              var detail = body && body.errors && body.errors[0] && body.errors[0].detail;
              self.showMessage(detail || 'Something went wrong. Please try again.');
            });
          }
        })
        .catch(function () {
          self.showMessage('Something went wrong. Please try again.');
        })
        .finally(function () {
          if (submitButton) submitButton.disabled = false;
        });
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
})();
